import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MagicWand, LinkedinLogo, TwitterLogo, InstagramLogo, EnvelopeSimple,
  ArrowRight, Sparkle, CalendarCheck, Waveform,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon } from "@phosphor-icons/react";

const PLATFORMS = [
  { icon: LinkedinLogo, label: "LinkedIn Post" },
  { icon: TwitterLogo, label: "X Thread" },
  { icon: InstagramLogo, label: "IG Caption" },
  { icon: EnvelopeSimple, label: "Newsletter" },
  { icon: Waveform, label: "Clip Ideas" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  return (
    <div className="min-h-screen bg-background grain relative overflow-hidden">
      {/* nav */}
      <header className="relative z-10 max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <MagicWand size={18} weight="fill" className="text-primary-foreground" />
          </div>
          <span className="font-heading font-black text-xl tracking-tight">PostPilot</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggle} data-testid="landing-theme-toggle" className="w-9 h-9 rounded-md border border-border flex items-center justify-center hover:bg-accent transition-colors">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Button variant="ghost" data-testid="landing-login-btn" onClick={() => navigate("/login")}>Log in</Button>
          <Button data-testid="landing-signup-btn" onClick={() => navigate("/register")}>Start free</Button>
        </div>
      </header>

      {/* hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card text-xs font-mono uppercase tracking-wide text-muted-foreground mb-6"
          >
            <Sparkle size={14} weight="fill" className="text-primary" /> One input · A full week of content
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]"
          >
            Turn one video into a<br />week of posts.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
            className="mt-6 text-base md:text-lg text-muted-foreground max-w-md leading-relaxed"
          >
            Drop in a YouTube link, podcast, or blog post. PostPilot repurposes it into LinkedIn posts, X threads, captions, a newsletter, and clip ideas — in your brand voice. Then schedules it all.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Button size="lg" data-testid="hero-cta" onClick={() => navigate("/register")} className="group">
              Start free trial <ArrowRight size={18} className="ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" data-testid="hero-secondary" onClick={() => navigate("/login")}>
              I have an account
            </Button>
          </motion.div>
          <p className="mt-4 text-xs text-muted-foreground font-mono">No card needed · 3 free repurposes</p>
        </div>

        {/* product mock */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-chart-4/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-chart-2/60" />
              <span className="ml-2 text-xs font-mono text-muted-foreground">postpilot / studio</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-border p-3 bg-background">
                <p className="text-xs font-mono text-muted-foreground mb-2">SOURCE</p>
                <div className="space-y-1.5">
                  <div className="h-2 rounded bg-muted w-full" />
                  <div className="h-2 rounded bg-muted w-4/5" />
                  <div className="h-2 rounded bg-muted w-5/6" />
                  <div className="h-2 rounded bg-muted w-2/3" />
                </div>
              </div>
              <div className="rounded-md border border-primary/40 p-3 bg-primary/5">
                <p className="text-xs font-mono text-primary mb-2 flex items-center gap-1"><LinkedinLogo size={12} weight="fill" /> LINKEDIN</p>
                <div className="space-y-1.5">
                  <div className="h-2 rounded bg-primary/30 w-full" />
                  <div className="h-2 rounded bg-primary/20 w-5/6" />
                  <div className="h-2 rounded bg-primary/20 w-full" />
                  <div className="h-2 rounded bg-primary/20 w-3/4" />
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <div key={p.label} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border text-xs bg-background">
                  <p.icon size={14} /> {p.label}
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-md bg-chart-2/10 border border-chart-2/30">
              <CalendarCheck size={16} className="text-chart-2" weight="fill" />
              <span className="text-xs font-medium">7 posts scheduled across the week</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* strip */}
      <section className="relative z-10 border-t border-border bg-card/40">
        <div className="max-w-6xl mx-auto px-6 py-10 grid sm:grid-cols-3 gap-8">
          {[
            { t: "Repurpose in seconds", d: "GPT-5.2 & Claude turn long content into platform-native posts." },
            { t: "Sound like you", d: "Tune tone, audience & style once — every post matches your voice." },
            { t: "Schedule & track", d: "Queue the week and see it all in a visual content calendar." },
          ].map((f) => (
            <div key={f.t}>
              <h3 className="font-heading font-bold text-lg mb-1">{f.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
